import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Award, Users, MapPin } from 'lucide-react';

const AboutPage = () => {
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/site-content`);
        setSiteContent(res.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  const stats = [
    { icon: Heart, value: '100+', label: 'Events Created' },
    { icon: Award, value: '5+', label: 'Years Experience' },
    { icon: Users, value: '100%', label: 'Client Satisfaction' },
    { icon: MapPin, value: 'All', label: 'Bahamas Islands' },
  ];

  const values = [
    {
      title: 'Creativity',
      description: 'We bring innovative ideas and unique perspectives to create one-of-a-kind celebrations that reflect your personality.',
    },
    {
      title: 'Attention to Detail',
      description: 'Every element matters. We meticulously plan and execute every aspect of your event to perfection.',
    },
    {
      title: 'Personal Care',
      description: 'We take the time to understand each client, ensuring your event is a true reflection of your vision.',
    },
  ];

  return (
    <div data-testid="about-page" className="bg-[#F5F5F0]">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1518173835991-88d87c9f3290?w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-[#4A5D23]/60" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <span className="font-accent text-4xl text-[#D4AF37] mb-4">Our Story</span>
          <h1 className="font-heading text-5xl md:text-7xl font-light">About Us</h1>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="font-accent text-3xl text-[#D4AF37]">Who We Are</span>
              <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2 mb-6">
                {siteContent?.about_title || 'Our Story'}
              </h2>
              <div className="w-20 h-0.5 bg-[#D4AF37] mb-8" />
              <p className="text-[#6B7280] leading-relaxed mb-6 text-lg">
                {siteContent?.about_content || 
                  `At Eden's Dawn Wedding Events and Creations, we combine creativity, personal attention and thoughtful design to create beautiful and meaningful events that reflect each client's vision and personality. Based in the beautiful Bahamas, we bring the essence of paradise to every celebration we craft.`
                }
              </p>
              <p className="text-[#6B7280] leading-relaxed mb-6">
                {siteContent?.about_mission || 
                  `Our mission is to take the time to understand each client, delivering professional care, creativity, and attention to detail that transforms your vision into an unforgettable reality.`
                }
              </p>
              <p className="text-[#6B7280] leading-relaxed">
                {siteContent?.about_values || 
                  `We believe in elegance, creativity, and stress-free planning experiences. Every event we create is specifically tailored to our client's desires, ensuring a unique celebration that tells your story.`
                }
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1519741497674-611481863552?w=800"
                  alt="Event planning"
                  className="rounded-2xl shadow-hover w-full"
                  data-testid="about-image-main"
                />
                <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-[#E8DFD0] rounded-2xl -z-10" />
                <div className="absolute -top-8 -right-8 w-32 h-32 border-2 border-[#D4AF37] rounded-2xl -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#4A5D23]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                data-testid={`stat-${index}`}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
                <div className="font-heading text-4xl text-white mb-2">{stat.value}</div>
                <div className="text-sm text-white/70 tracking-wider uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[#F5F5F0]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-accent text-3xl text-[#D4AF37]">What Guides Us</span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2">Our Values</h2>
            <div className="decorative-line" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-${index}`}
                className="bg-white p-10 rounded-2xl shadow-soft text-center group hover:shadow-hover transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-full bg-[#E8DFD0] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#D4AF37] transition-colors">
                  <span className="font-accent text-3xl text-[#D4AF37] group-hover:text-white transition-colors">{index + 1}</span>
                </div>
                <h3 className="font-heading text-2xl text-[#2C2C2C] mb-4">{value.title}</h3>
                <p className="text-[#6B7280]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-accent text-3xl text-[#D4AF37]">Where We Are</span>
              <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2 mb-6">
                Based in Paradise
              </h2>
              <div className="w-20 h-0.5 bg-[#D4AF37] mb-8" />
              <p className="text-[#6B7280] leading-relaxed mb-6 text-lg">
                We are proudly based in Nassau, The Bahamas, and we travel to serve all islands throughout our beautiful archipelago.
              </p>
              <p className="text-[#6B7280] leading-relaxed mb-8">
                Whether you're planning an intimate beach ceremony in the Exumas, a grand celebration in Nassau, or a destination event on any of our stunning islands, we bring our creativity and expertise to you.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="about-cta-btn"
                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-8"
                >
                  Get In Touch
                </Button>
              </Link>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1617170709050-d4d00fe97b4c?w=800"
                alt="Bahamas beach"
                className="rounded-2xl shadow-hover w-full"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
